<template>
  <div
    class="w-full h-full flex justify-center overflow-auto py-3 tablet-h:py-[14px]"
  >
    <div class="flex flex-col items-center m-auto">
      <div
        class="flex items-center justify-center bg-gray-200/30 rounded-xl mb-[43px]"
        :class="containerClasses"
      >
        <Lottie
          v-if="showAnimation"
          :name="animation"
          :class="animationClasses"
        />
      </div>

      <div
        v-if="showAnimation"
        class="font-semibold font-primary text-[20px] leading-[20px] tracking-[-0.25px] text-gray-800 mb-4"
      >
        {{ title }}
      </div>
      <Skeleton v-else class="w-[200px] h-5 mb-4" />

      <div
        class="font-secondary text-[14px] leading-[22px] text-gray-800 max-w-[302px] text-center mb-[26px]"
      >
        Drag and drop your original work to get copyright protection with Dacr
      </div>

      <UiButton
        icon="Upload"
        primary
        :disabled="disableUpload"
        @click="handleUploadClick"
      >
        Upload
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useExplorerStore } from '@/features/explorer/store'
import { useUserStore } from '@/shared/stores/user'
import { computed } from 'vue'
import Lottie from '@/shared/components/Lottie.vue'
import { Skeleton, UiButton } from '@dacr/ui'
import { useUploadFlow } from '@/features/upload/composables/useUploadFlow'
import { UserPermission } from '@/shared/graphql/legacy/generated'

const userStore = useUserStore()
const explorerStore = useExplorerStore()
const { showUploadDialog } = useUploadFlow()

const showUploadFirstFile = computed(
  () => (userStore.user?.totalFiles || 0) === 0
)
const showEmptyFolder = computed(
  () => !showUploadFirstFile.value && !explorerStore.currentFolder?.items.length
)

const showAnimation = computed(
  () => userStore.user && explorerStore.currentFolder
)

const containerClasses = computed(() => ({
  'size-[126px]': showUploadFirstFile.value,
  'w-[151px] h-[126px]': showEmptyFolder.value,
}))

const animationClasses = computed(() => ({
  'min-w-[160px]': showUploadFirstFile.value,
  'min-w-[170px]': showEmptyFolder.value,
}))

const animation = computed(() =>
  showUploadFirstFile.value ? 'New-File' : 'Empty-Folder'
)

const title = computed(() => {
  if (showUploadFirstFile.value) {
    return 'Add your first original work'
  }

  return 'This folder is empty'
})

const disableUpload = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Upload)
)

const handleUploadClick = () => {
  showUploadDialog()
}
</script>
